import { useCallback, useRef, useState } from "react";
import { Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { AgGridReact } from "ag-grid-react";

import { getLocaleText } from "../../actions/datagrid.js";
import { contextMenu, defaultColDef, gridSettings, sideBar, statusBar } from "../../common/grid.js";
import { procountorEmployeeExpandedQuery, vacancyQuery } from "../../api/index.js";
import { PdfReport } from "../../common/pdfreport.jsx";
import { activeIntegrationsSelector, authSelector } from "../../reducers/index.js";
import { dateFormatter, dateGetter } from "../../common/utils.jsx";

export const StatisticsTab = () => {
  const { t } = useTranslation();
  const gridRef = useRef();

  const { companyName } = useSelector(authSelector);
  const activeIntegrations = useSelector(activeIntegrationsSelector);

  const [report, setReport] = useState(null);
  const closeReport = () => setReport(null);

  const isProcountorActive = !!activeIntegrations.tokenProcountor;
  const {
    data,
    isLoading,
    refetch: getOldData,
  } = useQuery(isProcountorActive ? procountorEmployeeExpandedQuery : vacancyQuery);

  const procountorColumnDefs = [
    {
      headerName: t("PersonNumber", "Henkilönumero"),
      field: "employee.personNumber",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 80,
    },
    {
      headerName: t("Name", "Nimi"),
      valueGetter: (params) =>
        params.data && params.data.employee ? `${params.data.employee.lastName} ${params.data.employee.firstName}` : "",
      sortable: true,
      filter: "agMultiColumnFilter",
    },
    {
      headerName: t("VacationYear", "Lomavuosi"),
      field: "holidayCreditYear",
      sortable: true,
      filter: "agNumberColumnFilter",
      valueFormatter: ({ value }) => value && `${value}–${value+1}`,
      width: 100,
    },
    {
      headerName: t("HolidayEarningStartingDate", "Lomalaskennan aloituspäivämäärä"),
      valueGetter: (params) => params.data?.holidayPay?.holidayEarningStartDate ?? "",
      sortable: true,
      filter: "agDateColumnFilter",
      hide: true,
    },
    {
      headerName: t("HolidayBonusEarningStartingDate", "Lomarahalaskennan aloituspäivämäärä"),
      valueGetter: (params) => params.data?.holidayPay?.holidayBonusEarningStartDate ?? "",
      sortable: true,
      filter: "agDateColumnFilter",
      hide: true,
    },
    {
      headerName: t("RemainingVacationDays", "Lomapäiviä jäljellä"),
      valueGetter: (params) => params.data?.remainingHolidayDays ?? "",
      sortable: true,
      filter: "agNumberColumnFilter",
    },
/* // Hidden for now
    {
      headerName: t("RemainingHolidayPay", "Lomapalkkaa jäljellä"),
      valueGetter: (params) => params.data?.remainingHolidayPay ?? "",
      sortable: true,
      filter: "agNumberColumnFilter",
      hide: true,
    },
*/
    {
      headerName: t("RemainingVacationAllowanceDays", "Lomarahapäiviä jäljellä"),
      valueGetter: (params) => params.data?.remainingHolidayBonusDays ?? "",
      sortable: true,
      filter: "agNumberColumnFilter",
    },
    {
      headerName: t("RemainingExtraVacationDays", "Lisäpäiviä jäljellä"),
      valueGetter: (params) => params.data?.remainingAdditionalLeaveDays ?? "",
      sortable: true,
      filter: "agNumberColumnFilter",
    },
  ];

  const vacancyColumnDefs = [
    {
      headerName: t("Year", "Vuosi"),
      field: "vuosi",
      pivot: true,
      sortable: true,
    },
    {
      headerName: t("VacancyStart", "Lomanmääräytymisvuosi alkoi"),
      field: "alkoi",
      cellClass: "dateType",
      filter: "agDateColumnFilter",
      valueGetter: dateGetter,
      valueFormatter: dateFormatter,
    },
    {
      headerName: t("VacancyEnd", "Lomanmääräytymisvuosi päättyi"),
      field: "paattyi",
      cellClass: "dateType",
      filter: "agDateColumnFilter",
      valueGetter: dateGetter,
      valueFormatter: dateFormatter,
    },
    {
      headerName: t("NumberAbbr", "Nro"),
      field: "nro",
    },
    {
      headerName: t("Name", "Nimi"),
      field: "nimi",
      filter: "agMultiColumnFilter",
      rowGroup: true,
      enableRowGroup: true,
      sort: "asc",
    },
    {
      headerName: t("EarnedVacationDays", "Ansaitut lomapäivät"),
      field: "ap",
    },
    {
      headerName: t("UsedVacationDays", "Pidetyt lomapäivät"),
      field: "pid",
    },
    {
      headerName: t("RemainingVacationDays", "Lomapäiviä jäljellä"),
      field: "jalj",
      aggFunc: "sum",
      sortable: true,
    },
    {
      headerName: t("EarnedVacationAllowanceDays", "Ansaitut lomarahapäivät"),
      field: "lrans",
    },
    {
      headerName: t("UsedVacationAllowanceDays", "Pidetyt lomarahapäivät"),
      field: "lrpid",
    },
    {
      headerName: t("RemainingVacationAllowanceDays", "Lomarahapäiviä jäljellä"),
      valueGetter: ({ data }) =>
        data && data.lrans !== undefined && data.lrpid !== undefined ? data.lrans - data.lrpid : "",
    },
    {
      headerName: t("EarnedExtraVacationDays", "Ansaitut lisäpäivät"),
      field: "lisapans",
    },
    {
      headerName: t("UsedExtraVacationDays", "Pidetyt lisäpäivät"),
      field: "lisappid",
    },
    {
      headerName: t("RemainingExtraVacationDays", "Lisäpäiviä jäljellä"),
      valueGetter: ({ data }) =>
        data && data.lisapans !== undefined && data.lisappid !== undefined ? data.lisapans - data.lisappid : "",
    },
  ];

  const columnDefs = isProcountorActive ? procountorColumnDefs : vacancyColumnDefs;

  const getContextMenuItems = useCallback(
    (params) => {
      return contextMenu(t, params, setReport, {
        title: t("AnnualVacationStatistics", "Vuosilomatilastot"),
        companyname: companyName,
      });
    },
    [companyName, t],
  );

  return (
    <>
      {report !== null && <PdfReport json={report} onCloseReport={closeReport} />}
      <Row className="ag-theme-material styles-ag flex-grow-1">
        <AgGridReact
          {...gridSettings}
          loading={isLoading}
          ref={gridRef}
          columnDefs={columnDefs}
          defaultColDef={{ ...defaultColDef, filter: "agNumberColumnFilter" }}
          sideBar={sideBar}
          rowData={
            isProcountorActive
              ? (Array.isArray(data)
                ? data.flatMap((s) => {
                    const yrs = s?.holidayPay?.yearlyRemainings || [];
                    return yrs.map((yr) => ({
                      employee: s.employee,
                      holidayPay: s.holidayPay,
                      holidayCreditYear: yr?.holidayCreditYear,
                      remainingHolidayDays: yr?.remainingHolidayDays,
                      remainingHolidayPay: yr?.remainingHolidayPay,
                      remainingHolidayBonusDays: yr?.remainingHolidayBonusDays,
                      remainingAdditionalLeaveDays: yr?.remainingAdditionalLeaveDays,
                    }));
                  })
                : [])
              : (data?.vacancy_response?.lomarp ?? [])
          }
          pivotMode={!isProcountorActive}
          suppressAggFuncInHeader
          getContextMenuItems={getContextMenuItems}
          statusBar={statusBar}
          getLocaleText={(params) => getLocaleText(t, params)}
          animateRows
          suppressCellFocus
          enableCellTextSelection
          onFirstDataRendered={(params) => params.api.sizeColumnsToFit()}
          debug={import.meta.env.DEV}
        />
      </Row>
    </>
  );
};
